<template>
  <span class="statNum" :class="[{ bump: phase !== 'idle', primary }, phase]">{{ shown }}</span>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue'

/**
 * A polled stat that flashes when it changes.
 */

const props = withDefaults(defineProps<{
  value: number
  goodDirection?: 'up' | 'down'
  primary?: boolean
}>(), {
  goodDirection: 'up',
  primary: false,
})

type Phase = 'idle' | 'good' | 'bad'
const phase = ref<Phase>('idle')

/**
 * Lags `value` to the midpoint of the pulse, so the digits change while the
 * number is at full scale rather than as it starts to grow.
 */
const shown = ref(props.value)

let swapTimer: ReturnType<typeof setTimeout> | null = null
let endTimer: ReturnType<typeof setTimeout> | null = null

watch(() => props.value, (next, prev) => {
  // Don't animate the initial page load
  if (prev === undefined || next === prev) { shown.value = next; return }
  const rose = next > prev
  phase.value = (rose === (props.goodDirection === 'up')) ? 'good' : 'bad'

  const duration = props.primary ? 2200 : 1600
  if (swapTimer) clearTimeout(swapTimer)
  if (endTimer) clearTimeout(endTimer)
  swapTimer = setTimeout(() => { shown.value = next }, duration / 2)
  endTimer = setTimeout(() => { phase.value = 'idle' }, duration)
})

onBeforeUnmount(() => {
  if (swapTimer) clearTimeout(swapTimer)
  if (endTimer) clearTimeout(endTimer)
})
</script>

<style scoped>
.statNum {
  font-variant-numeric: tabular-nums;
  display: inline-block;
}

.bump {
  animation: statBump 1600ms cubic-bezier(0.37, 0, 0.63, 1);
}
.bump.primary {
  animation-duration: 2200ms;
}

.good { --statFlash: var(--color-accent); }
.bad { --statFlash: var(--color-text-tertiary); }

/* One continuous single pulse. */
@keyframes statBump {
  0%   { transform: scale(1);    color: inherit; }
  50%  { transform: scale(1.06); color: var(--statFlash); }
  100% { transform: scale(1);    color: inherit; }
}

@media (prefers-reduced-motion: reduce) {
  .bump { animation: statFade 1600ms cubic-bezier(0.37, 0, 0.63, 1); }
  @keyframes statFade {
    0%, 100% { color: inherit; }
    50% { color: var(--statFlash); }
  }
}
</style>
